import DOMPurify from 'dompurify';
import Cookies from 'js-cookie';
import Bugsnag from '@bugsnag/js';
import _ from 'lodash';
import i18n from 'i18next';
import moment from 'moment';
import queryString from 'qs';
import React from 'react';
import katex from 'katex';
import { isAuthenticated } from './AccessToken';
import {
	UTM_TAGS_TTL_DAYS,
	TUTOR_CHAT_CONFIGS,
	COOKIES,
	ALL_LANGUAGES_CODES,
	AVAILABLE_LANGUAGES,
	HOURS_IN_MONTH,
	ALL_COUNTRY_CODES,
	FRONTEND_URL,
	DEFAULT_MARKETING_LANGUAGE_LOCALE,
	IMAGES_TYPES,
	MAX_IMAGE_SIZE,
	IMAGES_EXTENSIONS,
	FILES_EXTENSIONS,
	MAX_FILE_SIZE,
	FILES_TYPES,
	AUTHENTICATION_COOKIES_SUFFIX,
	AUTHENTICATION_COOKIES_PREFIX,
	AUTHENTICATION_COOKIES_DOMAIN,
	DEFAULT_LANGUAGE_LOCALE,
	SOURCE_TYPES,
	IS_DEV_ENV,
	ZENDESK_CUSTOM_SUPPORT_URL,
	ZENDESK_CUSTOM_SUPPORT_URL_INTL,
	ZENDESK_NEW_REQUEST_URL,
	ZENDESK_NEW_REQUEST_URL_INTL,
	ZENDESK_SNIPPET_ID,
	ZENDESK_SNIPPET_ID_INTL,
	ZENDESK_IMPORT_LIST_URL,
	ZENDESK_IMPORT_LIST_URL_INTL,
	ZENDESK_PUBLISHER_MANUAL_URL,
	ZENDESK_PUBLISHER_MANUAL_URL_INTL,
	ZENDESK_REMOVED_QUESTION_OR_ANSWER_URL,
	ZENDESK_REMOVED_QUESTION_OR_ANSWER_URL_INTL,
	ZENDESK_CREATE_QUIZ_URL,
	ZENDESK_CREATE_QUIZ_URL_INTL,
	ZENDESK_CREATE_GROUP_URL,
	ZENDESK_CREATE_GROUP_URL_INTL,
	ZENDESK_SNIPPET_URL,
	ZENDESK_SNIPPET_URL_INTL,
	AUDIO_EXTENSIONS,
	AUDIO_TYPES,
	MAX_AUDIO_SIZE,
} from './Constants';
import { IS_MOBILE_APP, IS_ANDROID_APP } from './capacitor/Constants';
import { processRequest } from './Api';
import { historyPush, historyReplace } from './History';
import * as notificationActions from '../components/notification/Actions';
import * as onboardingModalActions from '../components/modals/onboarding/Actions';

import * as sharedActions from '../pagesNext/shared/Actions';
import { store } from '../store';
import { EVENTS_TYPES, MAX_FIRS_LAST_NAMES_LENGTH } from '../pagesNext/shared/Constants';

export function Timer(callback, delay) {
	let timerId;
	let start;
	let remaining = delay;

	this.pause = () => {
		window.clearTimeout(timerId);
		remaining -= new Date() - start;
	};

	this.resume = () => {
		start = new Date();
		window.clearTimeout(timerId);
		timerId = window.setTimeout(callback, remaining);
	};

	this.resume();
}

export const isSecure = () => {
	return window.location.protocol === 'https:';
};

export const practiceWords = ({ code, name, icon_name }, selectedItems, setSelectedWords, id) => {
	setSelectedWords({
		list_ids: Array.isArray(id) ? id : [id],
		selected_words: selectedItems,
		exercise_type_name: name,
		exercise_type_code: code,
		exercise_type_icon_name: icon_name,
	});
	historyPush('/start-exercise');
};

export const getIsSafari = () => {
	const userAgent = navigator.userAgent.toLowerCase();
	const isChrome = userAgent.indexOf('chrome') > -1;
	let isSafari = userAgent.indexOf('safari') > -1;
	if (isChrome && isSafari) isSafari = false;
	return isSafari;
};

export const removeUtmTags = (utmTagsObject) => {
	// eslint-disable-next-line guard-for-in
	for (const tag in utmTagsObject) {
		removeCookie(tag);
	}
};

export const setLastPageSlug = (lastPageSlug) => {
	setCookie(COOKIES.lastPageSlug, lastPageSlug, {
		expires: UTM_TAGS_TTL_DAYS,
		secure: isSecure(),
	});
};

export const deleteCharactersInString = (string, character) => {
	return string
		.split('')
		.filter((item) => item !== character)
		.join('');
};

export const nameInUrlFormat = (name) => {
	let convertedUrl = name;
	if (name) {
		if (name.includes('%')) {
			convertedUrl = deleteCharactersInString(name, '%');
		}

		if (name.includes('#')) {
			convertedUrl = deleteCharactersInString(convertedUrl, '#');
		}

		return convertedUrl
			.trim()
			.split(' ')
			.join('-')
			.split('/')
			.join('-')
			.split('.')
			.join('')
			.split('?')
			.join('')
			.toLowerCase();
	}
};

export const authenticateWebWidget = () => {
	window.zESettings = {
		webWidget: {
			authenticate: {
				chat: {
					connectOnPageLoad: true,
					jwtFn: async (callback) => {
						try {
							const {
								data: { jwt },
							} = await processRequest('users/chat_access_token');
							callback(jwt);
						} catch (e) {}
					},
				},
			},
			zIndex: 90,
			color: TUTOR_CHAT_CONFIGS.COLOR,
			chat: {
				suppress: false,
			},
			launcher: TUTOR_CHAT_CONFIGS.LAUNCHER,
			contactForm: {
				suppress: false,
				attachments: false,
				fields: false,
			},
			helpCenter: { suppress: true },
			talk: { suppress: true },
			answerBot: { suppress: true },
		},
	};
};

const authenticationCookieDomain = !IS_MOBILE_APP
	? undefined
	: { domain: AUTHENTICATION_COOKIES_DOMAIN };

const cookieOptions =
	!IS_MOBILE_APP && !IS_DEV_ENV
		? {
				path: '/',
				secure: true,
				sameSite: 'Strict',
		  }
		: {};

const getCookieName = (key) => AUTHENTICATION_COOKIES_PREFIX + key + AUTHENTICATION_COOKIES_SUFFIX;

export const getCookie = (key) => {
	return Cookies.get(getCookieName(key), authenticationCookieDomain);
};

function getExtendedOptions(options) {
	return IS_MOBILE_APP
		? _.cloneDeep(options)
		: _.extend(_.cloneDeep(options), authenticationCookieDomain);
}

export const setCookie = (key, val, options = {}) => {
	Cookies.set(getCookieName(key), val, getExtendedOptions({ ...options, ...cookieOptions }));
};

export const removeCookie = (key, options = {}) => {
	Cookies.remove(getCookieName(key), getExtendedOptions({ ...options, ...cookieOptions }));
};

export const getTimeRange = (created_at) => {
	const posted = new Date() - new Date(created_at);

	const years = Math.round(posted / 31557600000);
	const months = Math.round(posted / 2629800000);
	const weeks = Math.round(posted / 604800000);
	const days = Math.round(posted / 86400000);
	const hours = Math.round(posted / 3600000);
	const minutes = Math.round(posted / 60000);

	if (years >= 1) {
		return `${years} ${i18n.t('general.year')} ${i18n.t('general.past')}`;
	}
	if (months >= 1) {
		return `${months} ${i18n.t('general.month', { count: months })} ${i18n.t('general.past')}`;
	}
	if (weeks >= 1) {
		return `${weeks} ${i18n.t('general.week', { count: weeks })} ${i18n.t('general.past')}`;
	}
	if (days >= 1) {
		return `${days} ${i18n.t('general.day', { count: days })} ${i18n.t('general.past')}`;
	}
	if (hours >= 1) {
		return `${hours} ${i18n.t('general.hour')} ${i18n.t('general.past')}`;
	}

	return minutes <= 1
		? i18n.t('general.just_now')
		: `${minutes} ${i18n.t('general.minutes')} ${i18n.t('general.past')}`;
};

export const monthInHours = (count) => count * HOURS_IN_MONTH;

export const getMinutesFromSeconds = (seconds) => `±${Math.ceil(seconds / 60)} min`;

export const navigateToBookDetails = (history, bookId, bookName, bookGrade, bookEdition) => {
	historyReplace(
		`/books/${bookId}/${nameInUrlFormat(bookName)}-${nameInUrlFormat(
			bookGrade,
		)}-${nameInUrlFormat(bookEdition)}`,
	);
};

export const computedChapterTitle = (title, titleNumber) =>
	title || `${i18n.t('general.chapter')} ${titleNumber || i18n.t('general.not_applicable')}`;

export const isBootAgent = () =>
	window.navigator.userAgent.toLowerCase().includes('headlesschrome');

export const getSubcategoryFromCategories = (categories, subcategoryId) => {
	let selectedSubcategory = null;

	if (categories.length && subcategoryId) {
		categories.forEach((category) => {
			category.topic_subcategories.forEach((subcategory) => {
				if (subcategoryId.toString() === subcategory.id.toString()) {
					selectedSubcategory = subcategory;
				}
			});
		});
	}

	return selectedSubcategory;
};

export const validateVimeoVideoId = (e, setVideoId) => {
	const val = e.target ? e.target.value : e;

	if (val && val.trim()) {
		if (val.includes('vimeo.com')) {
			const valArray = val.split('/');
			const valId = valArray[valArray.length - 1];

			if (valId && valId.match(/^\d{8}/)) {
				if (setVideoId) setVideoId(valArray[valArray.length - 1]);
				return null;
			}
			return i18n.t('general.invalid_value');
		}
		if (!val.match(/^\d{8}/)) {
			return i18n.t('general.invalid_value');
		}
	}
};

export const setLocalStorageItem = (key, value) => {
	window.localStorage.setItem(key, value);
};
export const getLocalStorageItem = (key) => window.localStorage.getItem(key);

export const removeLocalStorageItem = (key) => {
	window.localStorage.removeItem(key);
};

export const setSessionStorageItem = (key, value) => {
	window.sessionStorage.setItem(key, value);
};
export const getSessionStorageItem = (key) => window.sessionStorage.getItem(key);
export const removeSessionStorageItem = (key) => window.sessionStorage.removeItem(key);

export const handleMarkdown = (Tag, props, children) => {
	if (Tag === 'em') {
		return <span className="green">{children}</span>;
	}

	return <Tag {...props}>{children}</Tag>;
};

export const convertImageURLToBlob = async (imageUrl) => {
	const response = await fetch(imageUrl);
	return response.blob();
};

export const formatDate = (date) => moment(date).format('DD-MM-YYYY');

export const formatTime = (date) => moment(date).format('HH:mm');

export const renderFormulas = (content, isEditor) => {
	const mathRegex = /\${1,2}(.*?)\${1,2}/g;

	let formattedContent = content || '';
	if (typeof content === 'object') formattedContent = JSON.stringify(content);
	if (formattedContent.search(mathRegex) < 0) {
		return disableContentEditableElements(formattedContent);
	}

	return disableContentEditableElements(formattedContent).replace(
		mathRegex,
		function (outer, inner) {
			let renderedFormula = '';
			try {
				const formula = katex.renderToString(inner);

				if (isEditor) {
					renderedFormula = `<span class="ql-formula" data-value="${inner}">${formula}</span>`;
				} else {
					renderedFormula = formula;
				}
			} catch (e) {
				renderedFormula = inner;
			}
			return renderedFormula;
		},
	);
};

export const disableContentEditableElements = (content, isEditor) => {
	if (!isEditor && content && content.includes('contenteditable="true"')) {
		return content.replaceAll('contenteditable="true"', 'contenteditable="false"');
	}
	return content || '';
};

export const getHTMLCollection = (innerHTML, classname) => {
	const tmp = document.createElement('div');
	tmp.innerHTML = innerHTML || '';
	return tmp.getElementsByClassName(classname);
};

export const encodingShareUrl = (url) => url.replace('%26', '&');

export const formatSecondsTime = (sec) => moment.utc(sec * 1000).format('mm:ss');

export const getRandomValueFromRange = (min, max) =>
	Math.floor(Math.random() * (max - min + 1)) + min;

export const isLanguageCode = (lngCode) => {
	return ALL_LANGUAGES_CODES.filter((item) => item.code === lngCode).length > 0;
};

export const isCountryCode = (countryCode) => {
	return ALL_COUNTRY_CODES.filter((item) => item.code === countryCode).length > 0;
};

export const getSystemPreferLanguage = () => {
	const { languages, language, browserLanguage } = window.navigator;
	let lang = languages ? languages[0] : null;
	lang = lang || language || browserLanguage;

	let shortLang = lang;
	if (shortLang.indexOf('-') !== -1) shortLang = shortLang.split('-')[0];
	if (shortLang.indexOf('_') !== -1) shortLang = shortLang.split('_')[0];

	return shortLang;
};

export const getLanguageLocale = () => {
	const { pathname } = window.location;
	const isLoggedIn = isAuthenticated();
	const language = pathname.split(`/`)[1];
	let selectedLanguage = getLocalStorageItem('selectedLanguage');
	const systemPreferLanguage = getSystemPreferLanguage();

	if (
		!isLoggedIn &&
		AVAILABLE_LANGUAGES &&
		AVAILABLE_LANGUAGES.includes(language) &&
		language !== selectedLanguage
	) {
		selectedLanguage = language;
		setLocalStorageItem('selectedLanguage', language);
	}

	if (!selectedLanguage && AVAILABLE_LANGUAGES) {
		const defaultLanguage =
			systemPreferLanguage && AVAILABLE_LANGUAGES.includes(systemPreferLanguage)
				? systemPreferLanguage
				: AVAILABLE_LANGUAGES[0];
		if (defaultLanguage !== selectedLanguage) {
			selectedLanguage = defaultLanguage;
			setLocalStorageItem('selectedLanguage', defaultLanguage);
		}
	}

	return selectedLanguage;
};

export const withLanguageLocale = (url) => {
	const locale = getLanguageLocale();

	return locale ? `/${locale}/learn${url}` : url;
};

export const getLocalizedLinks = (pathname) => {
	const path = pathname.split('/').slice(2).join('/');
	const locales = [...AVAILABLE_LANGUAGES, 'x-default'];

	return locales.map((item) => {
		const route = item === 'x-default' ? path : `${item}/${path}`;
		const url = `${FRONTEND_URL}/${route}`;
		return <link key={item} rel="alternate" href={url} hrefLang={item} />;
	});
};

export const setGTMData = (GTMData) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(GTMData);
};

export const getLocaleFromReferrer = () => {
	const referrerData = document.referrer;
	if (referrerData && referrerData !== '' && referrerData.includes(FRONTEND_URL)) {
		const locale = referrerData.split(`${FRONTEND_URL}/`)[1];
		const lang = locale && locale !== '' && locale.split('/')[0];

		return locale === '' || !isLanguageCode(lang) ? DEFAULT_MARKETING_LANGUAGE_LOCALE : lang;
	}
	return null;
};

export const getValByKey = (containsObj = {}, searchObj = {}, type = '') =>
	containsObj[Object.keys(searchObj).find((k) => searchObj[k] === type)];

export const hideSplashScreen = () => {
	setTimeout(function () {
		navigator.splashscreen.hide();
	}, 800);
};

export const availableLocales = {
	en: 'en-GB',
	de: 'de-DE',
	es: 'es-ES',
	fr: 'fr-FR',
	pl: 'pl-PL',
	pt: 'pt-BR',
	nl: 'nl-NL',
};

export const PURPLE = {
	light: '#652EA3',
	dark: '#240e3e',
};

export const validateFileType = (file, type) => {
	const fileType = file.name.slice(file.name.lastIndexOf('.') + 1);
	let types;
	switch (type) {
		case 'image':
			types = IMAGES_EXTENSIONS;
			break;
		case 'audio':
			types = AUDIO_EXTENSIONS;
			break;
		case 'file':
		default:
			types = FILES_EXTENSIONS;
			break;
	}
	return types.includes(fileType);
};

export const validateFileSize = (file, type) => {
	const fileSize = file.size;
	let maxSize = 0;
	switch (type) {
		case 'image':
			maxSize = MAX_IMAGE_SIZE;
			break;
		case 'audio':
			maxSize = MAX_AUDIO_SIZE;
			break;
		case 'file':
		default:
			maxSize = MAX_FILE_SIZE;
			break;
	}
	return fileSize <= maxSize;
};

export const validateFileContent = (file, type) => {
	const fileContent = file.type;
	let acceptedContents;
	switch (type) {
		case 'image':
			acceptedContents = IMAGES_TYPES;
			break;
		case 'audio':
			acceptedContents = AUDIO_TYPES;
			break;
		case 'file':
		default:
			acceptedContents = FILES_TYPES;
			break;
	}
	return acceptedContents.includes(fileContent);
};

export const scanFileForMaliciousContent = (file, t) => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		let isMalicious = false;
		reader.readAsBinaryString(file);
		reader.onload = () => {
			// Create an array of bytes from the binary string
			const byteArray = new Uint8Array(reader.result);

			isMalicious =
				reader.result.includes('<script>') || scanBytesForMaliciousContent(byteArray);
			resolve(isMalicious);
			if (isMalicious)
				store.dispatch(
					notificationActions.createNotification(t('general.extensions_notification')),
				);
		};
		return isMalicious;
	});
};

const scanBytesForMaliciousContent = (bytes) =>
	bytes.some((byte) => [0x90, 0xcc, 0xcd].includes(byte));

const getLimitsByType = (type) => {
	switch (type) {
		case 'image':
			return { size: MAX_IMAGE_SIZE / 1048576, extensions: IMAGES_EXTENSIONS.join(', ') };
		case 'audio':
			return { size: MAX_AUDIO_SIZE / 1048576, extensions: AUDIO_EXTENSIONS.join(', ') };
		case 'file':
		default:
			return { size: MAX_FILE_SIZE / 1048576, extensions: FILES_EXTENSIONS.join(', ') };
	}
};

export const uploadFile = async (file, callback, t, type, otherActions) => {
	if (validateFileType(file, type) && validateFileContent(file, type)) {
		if (await scanFileForMaliciousContent(file, t)) return;

		if (validateFileSize(file, type)) {
			callback();
		} else {
			store.dispatch(
				notificationActions.createNotification(
					t('general.limit_size_notification', { size: getLimitsByType(type).size }),
				),
			);

			if (otherActions) otherActions();
		}
	} else {
		store.dispatch(
			notificationActions.createNotification(
				t('general.extensions_notification', {
					extensions: getLimitsByType(type).extensions,
				}),
			),
		);
		if (otherActions) otherActions();
	}
};

export const removePropFromObject = (obj, prop) => {
	const { [prop]: removed, ...rest } = obj;
	return { ...rest };
};

export const getDangerouslySetInnerHTML = (content, withFormula) => {
	const sanitizedHTML = DOMPurify.sanitize(content);
	return {
		dangerouslySetInnerHTML: {
			__html: withFormula ? renderFormulas(sanitizedHTML) : sanitizedHTML,
		},
	};
};

export const filterKeysStartingWithUtm = (obj) => {
	return Object.entries(obj).reduce((filteredObj, [key, value]) => {
		if (key.startsWith('utm_')) {
			filteredObj[key] = value;
		}
		return filteredObj;
	}, {});
};

export const setMarketingTags = (search) => {
	const query = queryString.parse(search, { ignoreQueryPrefix: true });
	const utmItems = filterKeysStartingWithUtm(query);
	const { last_page_slug } = query || {};
	if (utmItems) {
		Object.entries(utmItems).forEach(([prop, value]) => {
			setCookie(prop, value, {
				expires: UTM_TAGS_TTL_DAYS,
				secure: isSecure(),
			});
		});
	}
	if (last_page_slug) setLastPageSlug(decodeURIComponent(last_page_slug));
};

export const getMarketingTags = (leaveUtmTags) => {
	const utmTags = Object.entries(Cookies.get()).reduce((acc, [key, value]) => {
		if (key.indexOf('utm_') !== -1) {
			const updatedKey = key
				.replace(AUTHENTICATION_COOKIES_PREFIX, '')
				.replace(AUTHENTICATION_COOKIES_SUFFIX, '');
			acc[updatedKey] = value;
		}
		return acc;
	}, {});
	const lastPageSlug = getCookie(COOKIES.lastPageSlug);
	if (utmTags && !leaveUtmTags) removeUtmTags(utmTags);
	if (lastPageSlug && !leaveUtmTags) removeCookie(COOKIES.lastPageSlug);

	return { ...utmTags, last_page_slug: lastPageSlug };
};

export const replaceHtmlEntities = (string) => {
	const htmlEntities = {
		'>': '&gt;',
		'<': '&lt;',
		'&': '&amp;',
		'"': '&quot;',
		"'": '&#039;',
	};

	return string.replace(/[<>&"']/g, (match) => htmlEntities[match]);
};

export const upgradeMessageSeen = (identifier) => {
	store.dispatch(onboardingModalActions.getOnboardingModalRequest({ identifier }));
	store.dispatch(
		sharedActions.sendEventRequest({
			event_name: EVENTS_TYPES.UPGRADE_MESSAGE_SEEN,
			source: SOURCE_TYPES.EXERCISE_DETAIL,
			feature: identifier,
		}),
	);
};

export const onCopyNotification = (t, error, copyText) => {
	if (error) {
		window.prompt(i18n.t('notifications.copied_to_clipboard'), copyText);
		return;
	}
	store.dispatch(
		notificationActions.createNotification(
			t('notifications.copied_to_clipboard_notification'),
			null,
			false,
			3000,
		),
	);
};

export const getFrontendUrl = (path) => FRONTEND_URL + withLanguageLocale(path);

export const getMarketingUrl = (path) => {
	const locale = getLanguageLocale();

	return `${FRONTEND_URL}/${locale}${path}`;
};

export const getDateTime = (datetime) => {
	const date = datetime ? new Date(datetime) : new Date();
	return date.toISOString();
};

// get save area for cordova app
export const getSafeAreaTop = () =>
	getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top').split('px')[0];

export const adjustTimezone = (momentDate) => {
	const now = new Date();
	const userTimeZoneOffset = -now.getTimezoneOffset();
	return momentDate.add(userTimeZoneOffset, 'minutes');
};

const createMomentDate = (date, format) => {
	return moment.utc(date, format);
};

export const formatLessonDescription = (date, t, duration, lessonStarting = false) => {
	const momentDate = createMomentDate(date, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
	const localDate = adjustTimezone(momentDate);
	const todayString = t('tutoring.lesson_details.scheduled_lessons.relative_dates.today');
	const tomorrowString = t('tutoring.lesson_details.scheduled_lessons.relative_dates.tomorrow');
	const formattedDay = localDate.calendar(null, {
		sameDay: `[${todayString}]`,
		nextDay: `[${tomorrowString}]`,
		nextWeek() {
			return `[${this.format('dddd')}]`;
		},
		sameElse() {
			return `[${this.format('dddd')}]`;
		},
	});

	const formattedDate = localDate.format('DD-MM');
	const formattedTime = localDate.format('HH:mm');

	return t('tutoring.welcome_screen.lesson', {
		context: lessonStarting ? 'starting' : 'at',
		date: `${formattedDay} ${
			formattedDay === todayString || formattedDay === tomorrowString ? '' : formattedDate
		}`,
		time: formattedTime,
		duration,
	});
};

export const formatDateToRelativeDate = (time, t, isISO) => {
	const date = isISO ? moment(time) : moment.unix(time);

	if (!date) return '';

	const today = moment().startOf('day');
	const tomorrow = moment().add(1, 'days').startOf('day');
	const startTime = date.format('HH:mm');

	let formattedDate = '';

	if (date.isSame(today, 'day')) {
		formattedDate = `${t('tutoring.lesson_details.scheduled_lessons.relative_dates.today')} ·
				${startTime}`;
	} else if (date.isSame(tomorrow, 'day')) {
		formattedDate = `${t(
			'tutoring.lesson_details.scheduled_lessons.relative_dates.tomorrow',
		)} · ${startTime}`;
	} else {
		formattedDate = date.format('dddd D MMMM · HH:mm');
	}

	return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};

export const countdownToTargetDate = (targetDate) => {
	const { date, time } = targetDate || {};
	const isValidTestDate =
		date &&
		time &&
		moment(date, 'DD-MM-YYYY', true).isValid() &&
		moment(time, 'HH:mm', true).isValid();

	if (isValidTestDate) {
		const dbTime = moment.utc(time, 'H:mm');
		const localTime = dbTime.clone().local().format('HH:mm');
		const combinedDateTime = `${date} ${localTime}`;
		const target = moment(combinedDateTime, 'DD-MM-YYYY HH:mm');
		const now = moment();
		const difference = target - now;
		const millisecondsInDay = 1000 * 60 * 60 * 24;
		const millisecondsInHour = 1000 * 60 * 60;

		if (difference > 0) {
			const days = Math.floor(difference / millisecondsInDay);
			const hours = Math.floor((difference % millisecondsInDay) / millisecondsInHour);
			const minutes = Math.floor((difference % millisecondsInHour) / (1000 * 60));
			return { days, hours, minutes };
		}
		return { days: 0, hours: 0, minutes: 0 };
	}
	Bugsnag.notify(new Error('Invalid date in countdownToTargetDate()'));
	return { days: 0, hours: 0, minutes: 0 };
};

export const isNlLocale = () => getLocalStorageItem('selectedLanguage') === DEFAULT_LANGUAGE_LOCALE;

const zendeskUrlMap = {
	zendesk_snippet_url: ZENDESK_SNIPPET_URL,
	zendesk_snippet_url_intl: ZENDESK_SNIPPET_URL_INTL,
	zendesk_snippet_id: ZENDESK_SNIPPET_ID,
	zendesk_snippet_id_intl: ZENDESK_SNIPPET_ID_INTL,
	zendesk_import_list_url: ZENDESK_IMPORT_LIST_URL,
	zendesk_import_list_url_intl: ZENDESK_IMPORT_LIST_URL_INTL,
	zendesk_publisher_manual_url: ZENDESK_PUBLISHER_MANUAL_URL,
	zendesk_publisher_manual_url_intl: ZENDESK_PUBLISHER_MANUAL_URL_INTL,
	zendesk_removed_question_or_answer_url: ZENDESK_REMOVED_QUESTION_OR_ANSWER_URL,
	zendesk_removed_question_or_answer_url_intl: ZENDESK_REMOVED_QUESTION_OR_ANSWER_URL_INTL,
	zendesk_create_quiz_url: ZENDESK_CREATE_QUIZ_URL,
	zendesk_create_quiz_url_intl: ZENDESK_CREATE_QUIZ_URL_INTL,
	zendesk_create_group_url: ZENDESK_CREATE_GROUP_URL,
	zendesk_create_group_url_intl: ZENDESK_CREATE_GROUP_URL_INTL,
	zendesk_custom_support_url: ZENDESK_CUSTOM_SUPPORT_URL,
	zendesk_custom_support_url_intl: ZENDESK_CUSTOM_SUPPORT_URL_INTL,
	zendesk_new_request_url: ZENDESK_NEW_REQUEST_URL,
	zendesk_new_request_url_intl: ZENDESK_NEW_REQUEST_URL_INTL,
};
export const getZendeskUrl = (link) => zendeskUrlMap[isNlLocale() ? link : `${link}_intl`];

export const getSystemTheme = () => {
	return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
		? 'system_dark'
		: 'system_light';
};

export const jsonParse = (val) => {
	try {
		return JSON.parse(val);
	} catch (error) {
		return null;
	}
};

export const formatLessonTimeslotToDisplay = (timeslot) => {
	const { starts_at, ends_at } = timeslot || {};

	if (starts_at && ends_at) {
		const dayName = moment(starts_at).format('dddd');
		const formattedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);

		return {
			text: `${formattedDayName} ${moment(starts_at).format('D MMMM')}`,
			subText: ` · ${moment(starts_at).format('HH:mm')} - ${moment(ends_at).format('HH:mm')}`,
		};
	}

	return {
		text: '',
		subText: '',
	};
};

export const getSanitizedText = (item) =>
	DOMPurify.sanitize(item, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).replace(/&nbsp;/g, ' ');

export const parseMarkdown = (markdown) =>
	markdown.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

export const getTruncatedName = (name = '') => {
	if (name?.length > MAX_FIRS_LAST_NAMES_LENGTH) {
		return `${name.slice(0, MAX_FIRS_LAST_NAMES_LENGTH)}...`;
	}
	return name;
};

export const getQueryFromUrl = (name) => {
	const urlParams = new URLSearchParams(window.location.search);
	const query = urlParams.get(name);
	return query || undefined;
};

export const copyToClipboard = (copyText, type = 'Text', callback) => {
	if (window.clipboardData && window.clipboardData.setData) {
		if (callback) callback();
		return window.clipboardData.setData(type, copyText);
	}

	if (!!window.cordova && window.cordova.plugins.clipboard) {
		window.cordova.plugins.clipboard.copy(copyText);
		if (callback) callback();
		return;
	}

	const copyField = document.createElement('input');
	document.body.appendChild(copyField);
	copyField.setAttribute('id', 'copyField');
	document.getElementById('copyField').value = copyText;
	copyField.select();

	try {
		document.execCommand('copy');
		document.body.removeChild(copyField);
		if (callback) callback();
	} catch (e) {
		if (callback) callback(true, copyText);
		document.body.removeChild(copyField);
	}
};

export const isTabletLandscape = () =>
	window.matchMedia('(orientation: landscape)').matches && window.innerWidth > 480;
